/**
 * The object UI's adapter over HTTP: every call goes to a path the manifest
 * declares or an href a record view served, never to one built per object.
 */
import { apiClient } from "./client";
import type { ConsignmentOrders, FoundAddress } from "../api-types";
import type {
  CommandBody,
  CommandEndpoint,
  ObjectUiAdapter,
} from "../object-ui-adapter";
import {
  isChosenFile,
  type ChildRow,
  type Command,
  type CommandResult,
  type DryRun,
  type JsonSchema,
  type Paginated,
  type RecordView,
  type TimelineEvent,
} from "../../domain/object-ui/contract";
import { toRequestParams } from "../../domain/object-ui/list-query";
import { fillPath } from "../../domain/object-ui/manifest";
import { timelineParams } from "../../domain/object-ui/timeline";
import { refOf } from "../../domain/object-ui/upload-field";

/**
 * A multipart command body: a chosen file goes as a file part, a string as
 * itself, anything else as JSON text. A keyed-files field (`keyed_by`) holds
 * `{ [key]: ChosenFile }`: each file goes as a part of the field and its key
 * as a part of the keys field, in the same order.
 */
export function multipartBody(
  body: CommandBody,
  schema: JsonSchema | null = null,
): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(body)) {
    if (value === undefined || value === null) continue;
    const keysField = schema?.properties?.[name]?.["x-cis-field"]?.keyed_by;
    if (keysField) {
      for (const [key, file] of Object.entries(
        value as Record<string, unknown>,
      )) {
        if (!isChosenFile(file)) continue;
        form.append(name, file.blob, file.name);
        form.append(keysField, key);
      }
    } else if (isChosenFile(value)) form.append(name, value.blob, value.name);
    else if (typeof value === "string") form.append(name, value);
    else form.append(name, JSON.stringify(value));
  }
  return form;
}

function send<T>(
  endpoint: CommandEndpoint,
  href: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body: CommandBody,
  signal?: AbortSignal,
): Promise<T> {
  if (endpoint.command.content_type === "multipart") {
    return apiClient.postForm<T>(
      href,
      multipartBody(body, endpoint.command.request_schema),
    );
  }
  return apiClient.send<T>(method, href, body, signal ? { signal } : undefined);
}

export function createRealObjectUiAdapter(): ObjectUiAdapter {
  return {
    list: (list, query) =>
      apiClient.get<Paginated<ChildRow>>(
        `${list.path}?${toRequestParams(query).toString()}`,
      ),
    getRecord: (record, key) => {
      const path = fillPath(record.path, { [record.path_param]: key });
      const include = record.include.map((token) => ["include", token]);
      return apiClient.get<RecordView>(
        include.length === 0
          ? path
          : `${path}?${new URLSearchParams(include).toString()}`,
      );
    },
    getTimeline: (record, key, query) =>
      apiClient.get<Paginated<TimelineEvent>>(
        `${fillPath(record.timeline_path ?? "", { [record.path_param]: key })}?${timelineParams(query).toString()}`,
      ),
    recordOpened: async (record, key) => {
      await apiClient.post<void>(record.on_open ?? "", {
        [record.path_param]: key,
      });
    },
    download: async (href) => {
      const { blob, filename } = await apiClient.getBlob(href);
      return { blob, filename };
    },
    listCollectionCommands: (objectId) =>
      apiClient.get<Command[]>(
        `/objects/${encodeURIComponent(objectId)}/commands`,
      ),
    // A dry run is always a POST, whatever the commit's method (platform spec §4.4).
    dryRun: (endpoint, body, signal) =>
      send<DryRun>(endpoint, endpoint.dryRunHref, "POST", body, signal),
    commit: (endpoint, body) =>
      send<CommandResult>(
        endpoint,
        endpoint.href,
        endpoint.command.method,
        body,
      ),
    // A POST: the postcode is where someone may live, so it travels in the body, never in a URL.
    lookupPostcode: (href, postcode) =>
      apiClient.post<FoundAddress[]>(href, { postcode }),
    upload: async (target, file) => {
      const form = new FormData();
      form.append(target.file_field, file.blob, file.name);
      return refOf(
        await apiClient.postForm<unknown>(target.path, form),
        target,
      );
    },
    findOrdersByConsignment: (reference) =>
      apiClient.get<ConsignmentOrders>(
        `/views/consignment-orders?${new URLSearchParams({ reference }).toString()}`,
      ),
  };
}
